import AvatarLogo from '@/assets/images/avatar-rounded.webp';
import { fetchImageAsBase64 } from '@/utils/images';
import { useUserStore } from '@/stores/useUserStore';
import type { Supplier, User } from '@/types/api';
import type { TableRow } from '@/types/order';
import { Capacitor } from '@capacitor/core';
import jsPDF from 'jspdf';
import { useToast } from 'primevue/usetoast';
import { useFileDownload } from './useFileDownload';

export const useOrder = () => {
  const toast = useToast();

  const convertWebpToPng = (imgSrc: string, maxWidth = 800): Promise<{ dataUrl: string; aspectRatio: number; format: 'PNG' | 'JPEG' }> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');

        // Calculate dimensions - resize if image is too large
        let width = img.width;
        let height = img.height;
        const aspectRatio = width / height;

        if (width > maxWidth) {
          width = maxWidth;
          height = width / aspectRatio;
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          resolve({
            dataUrl: canvas.toDataURL('image/png'),
            aspectRatio: aspectRatio,
            format: 'PNG',
          });
          return;
        }

        ctx.drawImage(img, 0, 0, width, height);

        // Check for transparency by scanning pixel data
        const imageData = ctx.getImageData(0, 0, width, height);
        const data = imageData.data;
        let hasTransparency = false;

        // Check alpha channel (every 4th value in the data array)
        for (let i = 3; i < data.length; i += 4) {
          if (data[i] < 255) {
            hasTransparency = true;
            break;
          }
        }

        // Use PNG for transparent images, JPEG for opaque images
        if (hasTransparency) {
          resolve({
            dataUrl: canvas.toDataURL('image/png'),
            aspectRatio: aspectRatio,
            format: 'PNG',
          });
        } else {
          resolve({
            dataUrl: canvas.toDataURL('image/jpeg', 0.8),
            aspectRatio: aspectRatio,
            format: 'JPEG',
          });
        }
      };
      img.src = imgSrc;
    });
  };

  function formatDate(date: Date): string {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  }

  // New helper function
  function renderIngredientsList(doc: jsPDF, ingredients: TableRow[], startY: number, margin: number, lineHeight: number): number {
    let y = startY;
    ingredients.forEach((ingredient) => {
      doc.circle(margin + 2, y - 1.5, 0.8, 'F');
      const data = ingredient.data as { quantity?: string; unit?: string; name: string };
      const quantityUnit = [data.quantity, data.unit].filter(Boolean).join(' ');
      const text = quantityUnit ? `${quantityUnit} - ${data.name}` : data.name;
      doc.text(text, margin + 5, y);
      y += lineHeight;
    });
    return y;
  }

  async function generatePDF(
    t: (key: string, params?: Record<string, unknown>) => string,
    ingredientsToOrder: TableRow[],
    supplierList: Supplier[],
    selectedSupplierUuid: string | null,
    wantedDate: Date,
    customMessage: string | null,
  ) {
    // Convert both logos
    const user = useUserStore().user as User;
    const { dataUrl: transparentPng, aspectRatio: avatarAspectRatio, format: avatarFormat } = await convertWebpToPng(AvatarLogo);
    const restaurantLogoUrl = user?.restaurants[0].logo_url ?? null;
    let restaurantLogoData;
    if (restaurantLogoUrl) {
      const base64 = await fetchImageAsBase64(restaurantLogoUrl);
      if (base64) {
        restaurantLogoData = await convertWebpToPng(base64);
      }
    }

    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });

    // Add fonts - only on web platform to avoid native compatibility issues
    let hasCustomFonts = false;
    if (!Capacitor.isNativePlatform()) {
      try {
        // Dynamically import fonts only for web platform
        const [OpenSansBold, OpenSansRegular, OpenSansLight] = await Promise.all([
          import('@/assets/fonts/open-sans/OpenSans-Bold.ttf'),
          import('@/assets/fonts/open-sans/OpenSans-Regular.ttf'),
          import('@/assets/fonts/open-sans/OpenSans-Light.ttf'),
        ]);

        doc.addFont(OpenSansBold.default, 'OpenSans-Bold', 'bold');
        doc.addFont(OpenSansRegular.default, 'OpenSans-Regular', 'regular');
        doc.addFont(OpenSansLight.default, 'OpenSans-Light', 'light');
        hasCustomFonts = true;
        console.log('✅ Custom OpenSans fonts loaded successfully for web platform');
      } catch (error) {
        console.warn('⚠️ Failed to load custom fonts on web, using default fonts:', error);
        hasCustomFonts = false;
      }
    } else {
      // Native platforms use system fonts to avoid compatibility issues
      console.log('📱 Using system fonts for native platform (recommended for compatibility)');
      hasCustomFonts = false;
    }

    // Font helper functions
    const setBoldFont = (size?: number) => {
      try {
        if (hasCustomFonts) {
          doc.setFont('OpenSans-Bold', 'bold');
        } else {
          doc.setFont('helvetica', 'bold');
        }
        if (size) doc.setFontSize(size);
      } catch {
        doc.setFont('helvetica', 'bold');
        if (size) doc.setFontSize(size);
      }
    };

    const setRegularFont = (size?: number) => {
      try {
        if (hasCustomFonts) {
          doc.setFont('OpenSans-Regular', 'regular');
        } else {
          doc.setFont('helvetica', 'normal');
        }
        if (size) doc.setFontSize(size);
      } catch {
        doc.setFont('helvetica', 'normal');
        if (size) doc.setFontSize(size);
      }
    };

    const setLightFont = (size?: number) => {
      try {
        if (hasCustomFonts) {
          doc.setFont('OpenSans-Light', 'light');
        } else {
          doc.setFont('helvetica', 'normal');
        }
        if (size) doc.setFontSize(size);
      } catch {
        doc.setFont('helvetica', 'normal');
        if (size) doc.setFontSize(size);
      }
    };

    // Page setup
    const margin = 20;
    const pageWidth = 210;
    const lineHeight = 8;
    const lineHeightTight = 6;
    let y = margin + lineHeight * 2;

    // TITLE //
    setBoldFont(16);
    doc.text(`${t('orders.orderForm.file.orderOfDate', { date: formatDate(new Date()) })} - ${user?.restaurants[0].name}`, pageWidth / 2, margin, {
      align: 'center',
    });

    // CONTENT //
    setRegularFont(12);

    // Supplier info with fallback for null supplier
    const supplier = supplierList.find((s) => s.uuid === selectedSupplierUuid) || {
      name: t('orders.noSupplier'),
    };
    doc.text(`${t('orders.orderForm.file.provider')}: ${supplier.name}`, margin, y);
    y += lineHeight;
    doc.text(`${t('orders.orderForm.file.deliveryDateWanted')}: ${formatDate(wantedDate)}`, margin, y);
    y += lineHeight;

    // Ingredients list
    doc.text(t('orders.orderForm.file.ingredientsList'), margin, y);
    y += lineHeight;
    setLightFont();

    const ingredientsPerPage = 15;
    const needsPagination = ingredientsToOrder.length >= 18;
    const totalPages = needsPagination ? 2 : 1;

    if (needsPagination) {
      // First page ingredients
      y = renderIngredientsList(doc, ingredientsToOrder.slice(0, ingredientsPerPage), y, margin, lineHeight);
      addFooter(doc, margin, transparentPng, avatarAspectRatio, t, 1, totalPages, setBoldFont, setRegularFont, setLightFont, avatarFormat);

      // Add new page
      doc.addPage();
      y = margin + lineHeight * 2;

      // Reset font settings for new page
      setRegularFont(12);
      doc.text(t('orders.orderForm.file.ingredientsList'), margin, y);
      y += lineHeight;
      setLightFont();

      // Second page ingredients
      y = renderIngredientsList(doc, ingredientsToOrder.slice(ingredientsPerPage), y, margin, lineHeight);
      addFooter(doc, margin, transparentPng, avatarAspectRatio, t, 2, totalPages, setBoldFont, setRegularFont, setLightFont, avatarFormat);
    } else {
      // Single page - all ingredients
      y = renderIngredientsList(doc, ingredientsToOrder, y, margin, lineHeight);
      addFooter(doc, margin, transparentPng, avatarAspectRatio, t, 1, 1, setBoldFont, setRegularFont, setLightFont, avatarFormat);
    }

    // Custom message
    if (customMessage) {
      y += lineHeight;
      setRegularFont(12);
      doc.text(t('orders.orderForm.file.customMessage'), margin, y);
      y += lineHeight;
      setLightFont();
      const messageLines = doc.splitTextToSize(customMessage, pageWidth - margin * 2);
      messageLines.forEach((line: string) => {
        doc.text(line, margin, y);
        y += lineHeight;
      });
    }

    // Restaurant info
    setRegularFont(12);
    y += lineHeight;
    doc.text(`${t('orders.orderForm.file.restaurant')}`, margin, y);

    // Restaurant Logo
    if (restaurantLogoData) {
      const maxLogoHeight = 30;
      const maxLogoWidth = 60;
      let logoHeight = maxLogoHeight;
      let logoWidth = logoHeight * restaurantLogoData.aspectRatio;

      if (logoWidth > maxLogoWidth) {
        logoWidth = maxLogoWidth;
        logoHeight = logoWidth / restaurantLogoData.aspectRatio;
      }

      const logoX = pageWidth - margin - logoWidth;
      const logoY = y;

      doc.addImage(restaurantLogoData.dataUrl, restaurantLogoData.format, logoX, logoY, logoWidth, logoHeight);
      // y += logoHeight + lineHeight;
    }

    y += lineHeightTight;

    // Content
    setLightFont();
    doc.text(`${user?.restaurants[0].name}`, margin, y);
    y += lineHeightTight;
    if (user?.restaurants[0].phone_number) {
      doc.text(`${user?.restaurants[0].phone_number}`, margin, y);
      y += lineHeightTight;
    }

    y += lineHeight; // Extra space after restaurant info

    // Footer section
    const pageHeight = 297;
    const footerY = pageHeight - margin / 2;
    const footerHeight = 15;
    const footerCenterY = footerY - footerHeight / 2;

    // Footer logo dimensions
    const logoHeight = 12;
    const maxLogoWidth = 24;
    let logoWidth = logoHeight * avatarAspectRatio;
    if (logoWidth > maxLogoWidth) {
      logoWidth = maxLogoWidth;
    }

    // Draw avatar logo on the left
    const logoY = footerCenterY - logoHeight / 2;
    doc.addImage(transparentPng, avatarFormat, margin, logoY, logoWidth, logoHeight);

    // Calculate text widths for left side
    setLightFont(7);
    const poweredByText = 'Powered by ';
    const poweredByWidth = doc.getTextWidth(poweredByText);

    setBoldFont();
    const gastronomiaText = 'GastronomIA';

    // Draw "Powered by GastronomIA" text vertically centered
    const textY = footerCenterY + 2;
    doc.setTextColor(23, 52, 71);
    setLightFont();
    doc.text(poweredByText, margin + logoWidth + 2, textY);
    setBoldFont();
    doc.text(gastronomiaText, margin + logoWidth + 2 + poweredByWidth, textY);

    // Reset text color for the rest of the document
    doc.setTextColor(0, 0, 0);

    // Save the PDF using useFileDownload
    const date = new Date().toISOString().replace(/[-:.TZ]/g, '');
    const supplierName = supplier.name ? `-${supplier.name}-` : `-${t('orders.noSupplier')}-`;
    const fileName = `${t('orders.orderForm.file.order')}${supplierName}${date}.pdf`;
    const { downloadFile } = useFileDownload();

    try {
      console.log('Generating PDF blob...');
      const pdfBlob = doc.output('blob');
      console.log('PDF blob created:', pdfBlob.size, 'bytes');

      await downloadFile({
        fileName,
        data: pdfBlob,
        mimeType: 'application/pdf',
        showShareDialog: true,
      });

      console.log('PDF download completed successfully');

      // Show success toast
      toast.add({
        severity: 'success',
        summary: t('orders.orderForm.file.downloadSuccess'),
        detail: t('orders.orderForm.file.downloadSuccessDetail'),
        life: 3000,
      });
    } catch (error) {
      console.error('Error downloading PDF:', error);

      // Show user-friendly error message
      const errorMessage = error instanceof Error ? error.message : 'Failed to generate PDF';
      console.error('PDF download failed:', errorMessage);

      toast.add({
        severity: 'error',
        summary: 'Download Failed',
        detail: errorMessage.includes('Permission denied')
          ? 'Unable to save PDF due to permission restrictions. Please check app permissions.'
          : errorMessage,
        life: 5000,
      });

      // Fallback to jsPDF's built-in save method (web only)
      console.log('Attempting fallback save...');
      try {
        doc.save(fileName);
        console.log('Fallback save successful');
        toast.add({
          severity: 'success',
          summary: 'PDF Downloaded',
          detail: 'PDF downloaded successfully using browser download',
          life: 3000,
        });
      } catch (fallbackError) {
        console.error('Fallback save also failed:', fallbackError);
        toast.add({
          severity: 'error',
          summary: 'Download Failed',
          detail: 'PDF generation failed. Please try again or contact support.',
          life: 5000,
        });
        throw new Error('PDF generation failed. Please try again or contact support.');
      }
    }
  }

  // Helper function to add footer
  function addFooter(
    doc: jsPDF,
    margin: number,
    logoImage: string,
    avatarAspectRatio: number,
    t: (key: string, params?: Record<string, unknown>) => string,
    currentPage: number,
    totalPages: number,
    setBoldFont: (size?: number) => void,
    setRegularFont: (size?: number) => void,
    setLightFont: (size?: number) => void,
    avatarFormat: 'PNG' | 'JPEG' = 'PNG',
  ) {
    const pageWidth = 210;
    const pageHeight = 297;
    const footerY = pageHeight - margin / 2;
    const footerHeight = 15;
    const footerCenterY = footerY - footerHeight / 2;

    // Footer logo dimensions
    const logoHeight = 12;
    const maxLogoWidth = 24;
    let logoWidth = logoHeight * avatarAspectRatio;
    if (logoWidth > maxLogoWidth) {
      logoWidth = maxLogoWidth;
    }

    // Draw avatar logo on the left
    const logoY = footerCenterY - logoHeight / 2;
    doc.addImage(logoImage, avatarFormat, margin, logoY, logoWidth, logoHeight);

    // Calculate text widths for left side
    setLightFont(7);
    const poweredByText = 'Powered by ';
    const poweredByWidth = doc.getTextWidth(poweredByText);

    // Draw "Powered by GastronomIA" text vertically centered
    const textY = footerCenterY + 2;
    doc.setTextColor(23, 52, 71);
    setLightFont();
    doc.text(poweredByText, margin + logoWidth + 2, textY);
    setBoldFont();
    doc.text('GastronomIA', margin + logoWidth + 2 + poweredByWidth, textY);

    // Add page numbers centered
    setRegularFont(10);
    doc.setTextColor(23, 52, 71);
    const pageText = t('orders.orderForm.file.pageOnPage', {
      page: currentPage,
      totalPage: totalPages,
    });
    doc.text(pageText, pageWidth - margin, footerCenterY + 2, { align: 'right' });

    // Reset text color
    doc.setTextColor(0, 0, 0);
  }

  return {
    generatePDF,
    formatDate,
  };
};
